import type { StatusItem } from '../components/ResourceDetail';
import { redactedJson } from '../lib/redaction';
import { formatSystemCopy, getSystemSettingsCopy, type SystemLocale } from './i18n';

export type SystemView = 'settings' | 'status' | 'release-operations';

export type ConsoleAssetEvidence = {
  assetPath?: string;
  assetCommit?: string;
  assetFingerprint?: string;
  expectedCommit?: string;
};

export type SystemSources = {
  systemOverview?: unknown;
  status?: unknown;
  personas?: unknown;
  sessions?: unknown;
  cron?: unknown;
  relay?: unknown;
  secrets?: unknown;
  audit?: unknown;
  assetEvidence?: ConsoleAssetEvidence;
  authClient?: {
    persistedToken?: boolean;
    storageKey?: string;
    logoutAvailable?: boolean;
    handlesAuthErrors?: boolean;
  };
  errors?: Record<string, string>;
};

export type SystemActionGuard = {
  id: string;
  label: string;
  state: string;
  reason: string;
  enabled: boolean;
  previewSupported: boolean;
  confirmationRequired: boolean;
  confirmationText: string;
};

export type SystemCutoverChecklistItem = {
  id: string;
  label: string;
  status: string;
  evidence: string;
};

export type SystemGlobalSettingsDraft = {
  language: string;
  logLevel: string;
  cronSilent: boolean;
  idleTimeoutMins: number;
  displayMode: string;
  displayThinkingMaxLen: number;
  displayToolMaxLen: number;
  displayRouteDebug: boolean;
  streamPreviewEnabled: boolean;
  streamPreviewIntervalMs: number;
  streamPreviewMinDeltaChars: number;
  streamPreviewMaxChars: number;
  streamPreviewDisabledPlatforms: string[];
  rateLimitEnabled: boolean;
  rateLimitMaxMessages: number;
  rateLimitWindowSecs: number;
  sessionCleanupEnabled: boolean;
  sessionCleanupInactiveDays: number;
  sessionCleanupKeepLatest: number;
};

export type SystemViewModel = {
  header: {
    title: string;
    description: string;
    meta: Array<[string, string]>;
  };
  status: StatusItem[];
  intentRows: Array<[string, string]>;
  serviceRows: Array<[string, string]>;
  healthRows: Array<[string, string]>;
  deploymentRows: Array<[string, string]>;
  authRows: Array<[string, string]>;
  globalSettingsRows: Array<[string, string]>;
  globalSettingsSourceRows: Array<[string, string]>;
  globalSettingsDraft: SystemGlobalSettingsDraft;
  globalSettingsDraftSupported: boolean;
  jsonRuleRows: Array<[string, string]>;
  sensitiveRevealRows: Array<[string, string]>;
  sensitiveAuditRows: Array<[string, string]>;
  runtimeRows: Array<[string, string]>;
  schedulerRows: Array<[string, string]>;
  platformRows: Array<[string, string]>;
  relaySecretRows: Array<[string, string]>;
  legacyRows: Array<[string, string]>;
  actionGuards: SystemActionGuard[];
  cutoverChecklist: SystemCutoverChecklistItem[];
  requiredBackendDTO: string[];
  rawJson: string;
  diagnosticsJson: string;
  partialNotices: string[];
  diagnosticNotices: string[];
};

const REQUIRED_BACKEND_DTO = [
  'GET /api/v1/system/overview',
  'build: { version, commit, built_at, tree_state }',
  'deployment: { console_asset_fingerprint, console_asset_commit, deployed_at, target_commit, freshness }',
  'health: { management_api, runtime_storage, scheduler, daemon, platform_adapters, relay, secret_registry }',
  'auth: { management_token, service_tokens: { present, count, mode, scope, scopes, allowed_routes, constraints, freshness }, http_errors }',
  'sensitive_reveal: { supported, token_reentry_required, reveal_endpoint, audit_action, audit_resource_kind } (contract gap: reveal endpoint missing)',
  'release_operations: { live_asset_evidence, cutover_checklist, operation_previews } (contract gap: preview+confirmation missing)',
  'actions: [{ id, label, preview_supported, confirmation_required, enabled, disabled_reason }]',
];

export function buildSystemViewModel(sources: SystemSources, view: SystemView, locale: SystemLocale = 'en-US'): SystemViewModel {
  const copy = getSystemSettingsCopy(locale);
  const systemOverview = asRecord(sources.systemOverview);
  const status = asRecord(sources.status);
  const deployment = deploymentEvidence(status, sources.assetEvidence, asRecord(systemOverview.deployment), locale);
  const runtime = asRecord(systemOverview.live_runtime);
  const health = asRecord(systemOverview.health);
  const auth = asRecord(systemOverview.auth);
  const globalSettings = asRecord(systemOverview.global_settings);
  const globalSettingsSource = asRecord(globalSettings.source);
  const sensitiveReveal = asRecord(systemOverview.sensitive_reveal);
  const releaseOperations = asRecord(systemOverview.release_operations);
  const liveAssetEvidence = asRecord(releaseOperations.live_asset_evidence);
  const serviceLive = Object.keys(status).length > 0 || Object.keys(runtime).length > 0;
  const scheduler = schedulerEvidence(status, sources.cron, sources.errors?.cron, runtime, locale);
  const platforms = platformEvidence(sources.status, sources.personas, runtime, health, locale);
  const secretRows = secretEvidence(sources.secrets, asRecord(health.secret_registry), asRecord(systemOverview.secret_registry));
  const actionGuards = guardedActions(systemOverview.actions, locale);
  const revealAudit = revealAuditEvidence(sources.audit);
  const cutoverChecklist = cutoverChecklistRows(releaseOperations, locale);
  const globalSettingsDraft = systemGlobalSettingsDraft(asRecord(globalSettings.editable));
  const deploymentRows: Array<[string, string]> = [
    [copy.labels.freshness, deployment.freshness],
    [copy.labels.artifactState, deployment.artifactState],
    [copy.labels.assetPath, sources.assetEvidence?.assetPath || copy.states.unavailable],
    [copy.labels.assetCommit, sources.assetEvidence?.assetCommit || deployment.assetCommit],
    [copy.labels.assetFingerprint, sources.assetEvidence?.assetFingerprint || deployment.assetFingerprint || copy.states.unavailable],
    [copy.labels.targetCommit, deployment.targetCommit],
    [copy.labels.evidenceSource, deployment.evidence],
    [copy.labels.backendAssetEvidence, backendAssetEvidence(liveAssetEvidence, deployment, copy)],
  ];
  const authRows: Array<[string, string]> = [
    [copy.labels.managementToken, tokenStatus(asRecord(auth.management_token), locale)],
    [copy.labels.serviceTokens, serviceTokenStatus(asRecord(auth.service_tokens), locale)],
    [copy.labels.tokenMode, authMode(status, auth, locale)],
    [copy.labels.persistedToken, presentLabel(sources.authClient?.persistedToken, locale)],
    [copy.labels.tokenStorage, sources.authClient?.storageKey || copy.states.unavailable],
    [copy.labels.logout, sources.authClient?.logoutAvailable ? copy.states.available : copy.states.unavailable],
    [copy.labels.httpAuthErrors, httpAuthErrorStatus(auth, sources.authClient, locale)],
    [copy.labels.secretRegistryEntries, String(secretRows.total)],
    [copy.labels.resolvedSecretRefs, String(secretRows.resolved)],
    [copy.labels.secretValues, copy.states.neverRendered],
  ];
  const globalSettingsRows: Array<[string, string]> = [
    [copy.labels.globalScope, copy.states.globalOnly],
    [copy.labels.language, globalSettingsDraft.language],
    [copy.labels.logLevel, globalSettingsDraft.logLevel],
    [copy.labels.cronSilent, booleanState(globalSettingsDraft.cronSilent, locale)],
    [copy.labels.displayMode, globalSettingsDraft.displayMode],
    [copy.labels.streamPreview, booleanState(globalSettingsDraft.streamPreviewEnabled, locale)],
    [copy.labels.rateLimit, globalSettingsDraft.rateLimitEnabled ? formatSystemCopy(copy.states.enabledLimit, { count: globalSettingsDraft.rateLimitMaxMessages, seconds: globalSettingsDraft.rateLimitWindowSecs }) : copy.states.disabled],
    [copy.labels.sessionCleanup, globalSettingsDraft.sessionCleanupEnabled ? formatSystemCopy(copy.states.enabledCleanup, { days: globalSettingsDraft.sessionCleanupInactiveDays, keep: globalSettingsDraft.sessionCleanupKeepLatest }) : copy.states.disabled],
    [copy.labels.managementApiIntent, configuredLabel(asRecord(systemOverview.configured_intent).management_api, locale)],
    [copy.labels.webConsoleIntent, configuredLabel(asRecord(systemOverview.configured_intent).web_console, locale)],
    [copy.labels.schedulerIntent, configuredLabel(asRecord(systemOverview.configured_intent).scheduler, locale)],
    [copy.labels.relayBridgeIntent, configuredLabel(asRecord(systemOverview.configured_intent).relay_bridge, locale)],
    [copy.labels.personaConfigBoundary, copy.states.personaConfigSeparated],
  ];
  const globalSettingsSourceRows: Array<[string, string]> = [
    [copy.labels.prefillSource, text(globalSettingsSource.state) || copy.states.partialData],
    [copy.labels.draftPersistence, globalSettings.draft_supported === true ? copy.states.available : text(globalSettings.draft_contract) || copy.states.needsBackendDTO],
    [copy.labels.configPath, text(globalSettingsSource.path) || copy.states.unavailable],
    [copy.labels.evidenceSource, text(globalSettingsSource.evidence) || copy.states.needsBackendDTO],
  ];
  const jsonRuleRows: Array<[string, string]> = [
    [copy.labels.viewConfigJsonEntrypoint, copy.states.singleEntrypoint],
    [copy.labels.draftJsonDrawer, copy.states.draftDrawerOnly],
    [copy.labels.draftPublishedDiff, copy.states.diffRequiredWhenEditing],
  ];
  const sensitiveRevealRows: Array<[string, string]> = [
    [copy.labels.secretValues, copy.states.neverRendered],
    [copy.labels.reenterToken, boolRequiredLabel(sensitiveReveal.token_reentry_required, locale)],
    [copy.labels.revealSupport, revealSupportLabel(sensitiveReveal, locale)],
    [copy.labels.revealContract, text(sensitiveReveal.contract_gap) || copy.states.needsBackendDTO],
    [copy.labels.auditAction, text(sensitiveReveal.audit_action) || 'secret.reveal'],
  ];
  const sensitiveAuditRows: Array<[string, string]> = [
    [copy.labels.auditEndpoint, text(sensitiveReveal.audit_endpoint) || 'GET /api/v1/audit?action=secret.reveal&resource_kind=secret'],
    [copy.labels.auditRows, String(revealAudit.count)],
    [copy.labels.latestAuditEvidence, revealAudit.latest || copy.states.noAuditEvidence],
    [copy.labels.auditSecretValues, copy.states.neverRendered],
  ];
  const visibleNotices = partialNotices(sources.errors, deployment, locale);
  const hasSystemOverview = Object.keys(systemOverview).length > 0;
  const diagnosticNotices = hasSystemOverview ? visibleNotices : [...visibleNotices, copy.notices.systemOverviewGap];

  return {
    header: headerFor(view, locale),
    status: [
      {
        label: copy.status.service,
        value: serviceLive ? copy.states.live : copy.states.partialData,
        evidence: serviceLive ? '/api/v1/status' : copy.evidence.statusEndpointUnavailable,
        tone: serviceLive ? 'green' : 'yellow',
      },
      {
        label: copy.status.deployment,
        value: deployment.freshness,
        evidence: deployment.evidence,
        tone: deployment.tone,
      },
      {
        label: copy.status.scheduler,
        value: scheduler.status,
        evidence: scheduler.evidence,
        tone: scheduler.tone,
      },
      {
        label: copy.status.actions,
        value: copy.states.guarded,
        evidence: copy.reasons.previewConfirmationUnavailable,
        tone: 'muted',
      },
    ],
    intentRows: intentRows(asRecord(systemOverview.configured_intent), locale),
    serviceRows: [
      [copy.labels.version, firstText([asRecord(systemOverview.build).version, status.version, asRecord(systemOverview.deployment).version]) || copy.states.unavailable],
      [copy.labels.commit, firstText([asRecord(systemOverview.build).commit, asRecord(systemOverview.deployment).commit]) || copy.states.unavailable],
      [copy.labels.builtAt, firstText([asRecord(systemOverview.build).built_at, asRecord(systemOverview.deployment).built_at]) || copy.states.unavailable],
      [copy.labels.uptime, formatDurationValue(status.uptime_seconds, locale)],
      [copy.labels.webConsoleAssets, boolAvailability(status.web_console, locale)],
      [copy.labels.managementApi, serviceLive ? copy.states.reachable : copy.states.partialData],
    ],
    healthRows: healthRows(health, serviceLive, locale),
    deploymentRows,
    authRows,
    globalSettingsRows,
    globalSettingsSourceRows,
    globalSettingsDraft,
    globalSettingsDraftSupported: globalSettings.draft_supported === true,
    jsonRuleRows,
    sensitiveRevealRows,
    sensitiveAuditRows,
    runtimeRows: [
      [copy.labels.personas, numberText(firstValue(status, ['personas_count', 'projects_count'], getArray(asRecord(sources.personas).personas).length))],
      [copy.labels.managementApiRuntime, text(runtime.management_api) || (serviceLive ? 'reachable' : copy.states.partialData)],
      [copy.labels.liveSessions, numberText(firstValue(status, ['live_sessions_count'], liveSessionCount(sources.sessions)))],
      [copy.labels.runtimeProfiles, numberText(runtime.runtime_profiles_count ?? runtimeProfileCount(sources.sessions))],
      [copy.labels.runtimeStorageHealth, statusWithEvidence(asRecord(health.runtime_storage), text(status.runtime_storage_status) || text(runtime.runtime_storage) || copy.states.needsBackendDTO)],
    ],
    schedulerRows: [
      [copy.labels.schedulerStatus, scheduler.status],
      [copy.labels.totalJobs, String(scheduler.totalJobs)],
      [copy.labels.enabledJobs, String(scheduler.enabledJobs)],
      [copy.labels.daemonStatus, statusWithEvidence(asRecord(health.daemon), text(status.daemon_status) || copy.states.needsBackendDTO)],
    ],
    platformRows: platforms,
    relaySecretRows: [
      [copy.labels.relayBindings, numberText(firstValue(status, ['relay_bindings_count'], getArray(asRecord(sources.relay).bindings).length))],
      [copy.labels.relayHealth, statusWithEvidence(asRecord(health.relay), sources.relay ? copy.states.relayReachable : copy.states.partialData)],
      [copy.labels.secretRegistryEntries, String(secretRows.total)],
      [copy.labels.resolvedSecretRefs, String(secretRows.resolved)],
      [copy.labels.missingSecretRefs, String(secretRows.missing)],
      [copy.labels.unsupportedSecretRefs, String(secretRows.unsupported)],
    ],
    legacyRows: [
      [copy.labels.legacyHarness, text(runtime.legacy_bridge) || copy.states.retiredLegacy],
      [copy.labels.relayBindings, numberText(firstValue(status, ['relay_bindings_count'], getArray(asRecord(sources.relay).bindings).length))],
      [copy.labels.legacyBridgeState, text(runtime.legacy_bridge) || (sources.relay ? copy.states.relayReachable : copy.states.partialData)],
    ],
    actionGuards,
    cutoverChecklist,
    requiredBackendDTO: REQUIRED_BACKEND_DTO,
    rawJson: redactedJson({
      view,
      global_settings: rowsToObject(globalSettingsRows),
      draftable_global_settings: globalSettingsDraft,
      auth_status: rowsToObject(authRows),
      deployment_freshness: rowsToObject(deploymentRows),
      json_rule: rowsToObject(jsonRuleRows),
    }, { redactPromptContent: true }),
    diagnosticsJson: redactedJson({ ...sources, required_backend_dto: REQUIRED_BACKEND_DTO }, { redactPromptContent: true }),
    partialNotices: visibleNotices,
    diagnosticNotices,
  };
}

function headerFor(view: SystemView, locale: SystemLocale): SystemViewModel['header'] {
  const copy = getSystemSettingsCopy(locale);
  if (view === 'status') {
    return {
      title: copy.header.statusTitle,
      description: copy.header.statusDescription,
      meta: [[copy.header.kind, copy.header.statusKind], [copy.header.source, 'Management API'], [copy.header.mode, copy.header.readOnly]],
    };
  }
  if (view === 'release-operations') {
    return {
      title: copy.header.releaseTitle,
      description: copy.header.releaseDescription,
      meta: [[copy.header.kind, copy.header.releaseKind], [copy.header.source, 'Status + static evidence'], [copy.header.mode, copy.header.guardedReadOnly]],
    };
  }
  return {
    title: copy.header.settingsTitle,
    description: copy.header.settingsDescription,
    meta: [[copy.header.kind, copy.header.settingsKind], [copy.header.source, 'Management API'], [copy.header.mode, copy.header.structuredDraft]],
  };
}

export function emptySystemGlobalSettingsDraft(): SystemGlobalSettingsDraft {
  return systemGlobalSettingsDraft({});
}

export function systemGlobalSettingsDraft(value: Record<string, unknown>): SystemGlobalSettingsDraft {
  const display = asRecord(value.display);
  const streamPreview = asRecord(value.stream_preview);
  const rateLimit = asRecord(value.rate_limit);
  const sessionCleanup = asRecord(value.session_cleanup);
  return {
    language: text(value.language) || 'en',
    logLevel: text(value.log_level) || 'info',
    cronSilent: booleanValue(value.cron_silent, false),
    idleTimeoutMins: numberWithDefault(value.idle_timeout_mins, 120),
    displayMode: text(display.mode) || 'default',
    displayThinkingMaxLen: numberWithDefault(display.thinking_max_len, 300),
    displayToolMaxLen: numberWithDefault(display.tool_max_len, 500),
    displayRouteDebug: booleanValue(display.route_debug, true),
    streamPreviewEnabled: booleanValue(streamPreview.enabled, true),
    streamPreviewIntervalMs: numberWithDefault(streamPreview.interval_ms, 1500),
    streamPreviewMinDeltaChars: numberWithDefault(streamPreview.min_delta_chars, 30),
    streamPreviewMaxChars: numberWithDefault(streamPreview.max_chars, 2000),
    streamPreviewDisabledPlatforms: getStrings(streamPreview.disabled_platforms),
    rateLimitEnabled: booleanValue(rateLimit.enabled, true),
    rateLimitMaxMessages: numberWithDefault(rateLimit.max_messages, 20),
    rateLimitWindowSecs: numberWithDefault(rateLimit.window_secs, 60),
    sessionCleanupEnabled: booleanValue(sessionCleanup.enabled, false),
    sessionCleanupInactiveDays: numberWithDefault(sessionCleanup.inactive_days, 30),
    sessionCleanupKeepLatest: numberWithDefault(sessionCleanup.keep_latest, 3),
  };
}

function deploymentEvidence(status: Record<string, unknown>, assetEvidence: ConsoleAssetEvidence | undefined, overviewDeployment: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  const overviewState = text(overviewDeployment.artifact_state);
  if (overviewState) {
    const artifactState = localizedState(overviewState, locale);
    return {
      freshness: artifactState,
      artifactState,
      assetCommit: text(overviewDeployment.console_asset_commit) || assetEvidence?.assetCommit || copy.states.unavailable,
      assetFingerprint: text(overviewDeployment.console_asset_fingerprint) || assetEvidence?.assetFingerprint || '',
      evidence: localizedEvidence(text(overviewDeployment.evidence) || copy.evidence.systemOverviewDeployment, locale),
      targetCommit: text(overviewDeployment.target_commit) || assetEvidence?.expectedCommit || copy.states.unavailable,
      tone: overviewState === 'fresh' ? 'green' as const : overviewState === 'unavailable' ? 'red' as const : 'yellow' as const,
    };
  }
  if (assetEvidence?.assetCommit && assetEvidence.expectedCommit) {
    if (sameCommit(assetEvidence.assetCommit, assetEvidence.expectedCommit)) {
      return { freshness: copy.states.fresh, artifactState: copy.states.fresh, assetCommit: assetEvidence.assetCommit, assetFingerprint: assetEvidence.assetFingerprint || '', evidence: copy.evidence.assetCommitMatchesExpectedTarget, targetCommit: assetEvidence.expectedCommit, tone: 'green' as const };
    }
    return { freshness: locale === 'zh-CN' ? '陈旧证据' : 'Stale evidence', artifactState: locale === 'zh-CN' ? '陈旧' : 'Stale', assetCommit: assetEvidence.assetCommit, assetFingerprint: assetEvidence.assetFingerprint || '', evidence: copy.evidence.assetCommitDiffersExpectedTarget, targetCommit: assetEvidence.expectedCommit, tone: 'yellow' as const };
  }
  if (status.web_console === false) {
    return { freshness: copy.states.unavailable, artifactState: copy.states.unavailable, assetCommit: assetEvidence?.assetCommit || copy.states.unavailable, assetFingerprint: assetEvidence?.assetFingerprint || '', evidence: copy.evidence.webConsoleFalse, targetCommit: assetEvidence?.expectedCommit || copy.states.unavailable, tone: 'red' as const };
  }
  return { freshness: copy.states.partialData, artifactState: copy.states.unknown, assetCommit: assetEvidence?.assetCommit || copy.states.unavailable, assetFingerprint: assetEvidence?.assetFingerprint || '', evidence: copy.evidence.assetCommitBuildMetadataUnavailable, targetCommit: assetEvidence?.expectedCommit || copy.states.unavailable, tone: 'yellow' as const };
}

function backendAssetEvidence(row: Record<string, unknown>, deployment: ReturnType<typeof deploymentEvidence>, copy: ReturnType<typeof getSystemSettingsCopy>) {
  return text(row.evidence) || text(row.console_asset_fingerprint) || deployment.assetFingerprint || text(row.contract_gap) || copy.states.needsBackendDTO;
}

function schedulerEvidence(status: Record<string, unknown>, cron: unknown, cronError: string | undefined, runtime: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (cronError) {
    return { status: copy.states.partialData, evidence: cronError, tone: 'yellow' as const, totalJobs: numberValue(status.cron_jobs_count), enabledJobs: 0 };
  }
  if (runtime.scheduler) {
    return { status: localizedState(text(runtime.scheduler), locale), evidence: 'GET /api/v1/system/overview live_runtime.scheduler', tone: runtime.scheduler === 'running' ? 'green' as const : 'blue' as const, totalJobs: numberValue(status.cron_jobs_count), enabledJobs: numberValue(status.cron_jobs_count) };
  }
  const jobs = getArray(asRecord(cron).jobs);
  if (jobs.length || cron) {
    return {
      status: copy.states.available,
      evidence: '/api/v1/cron',
      tone: 'green' as const,
      totalJobs: jobs.length,
      enabledJobs: jobs.filter(job => asRecord(job).enabled !== false).length,
    };
  }
  if (status.cron_jobs_count !== undefined) {
    return { status: copy.states.statusCountOnly, evidence: '/api/v1/status cron_jobs_count', tone: 'blue' as const, totalJobs: numberValue(status.cron_jobs_count), enabledJobs: numberValue(status.cron_jobs_count) };
  }
  return { status: copy.states.partialData, evidence: 'cron scheduler API unavailable', tone: 'yellow' as const, totalJobs: 0, enabledJobs: 0 };
}

function intentRows(intent: Record<string, unknown>, locale: SystemLocale): Array<[string, string]> {
  const copy = getSystemSettingsCopy(locale);
  return [
    [copy.labels.managementApiIntent, configuredLabel(intent.management_api, locale)],
    [copy.labels.webConsoleIntent, configuredLabel(intent.web_console, locale)],
    [copy.labels.schedulerIntent, configuredLabel(intent.scheduler, locale)],
    [copy.labels.relayBridgeIntent, configuredLabel(intent.relay_bridge, locale)],
  ];
}

function healthRows(health: Record<string, unknown>, serviceLive: boolean, locale: SystemLocale): Array<[string, string]> {
  const copy = getSystemSettingsCopy(locale);
  return [
    [copy.labels.managementApi, statusWithEvidence(asRecord(health.management_api), serviceLive ? copy.states.reachable : copy.states.partialData)],
    [copy.labels.runtimeStorageHealth, statusWithEvidence(asRecord(health.runtime_storage), copy.states.needsBackendDTO)],
    [copy.labels.schedulerStatus, statusWithEvidence(asRecord(health.scheduler), copy.states.needsBackendDTO)],
    [copy.labels.daemonStatus, statusWithEvidence(asRecord(health.daemon), copy.states.needsBackendDTO)],
    [copy.labels.platformAdapters, statusWithEvidence(asRecord(health.platform_adapters), copy.states.needsBackendDTO)],
    [copy.labels.relayHealth, statusWithEvidence(asRecord(health.relay), copy.states.needsBackendDTO)],
    [copy.labels.secretRegistryCounts, statusWithEvidence(asRecord(health.secret_registry), copy.states.needsBackendDTO)],
  ];
}

function platformEvidence(statusValue: unknown, personasValue: unknown, runtime: Record<string, unknown>, health: Record<string, unknown>, locale: SystemLocale): Array<[string, string]> {
  const connected = new Set(getStrings(asRecord(statusValue).connected_platforms));
  for (const platform of getStrings(runtime.connected_platforms)) connected.add(platform);
  const platformAdapters = asRecord(health.platform_adapters);
  for (const platform of getStrings(platformAdapters.connected_platforms)) connected.add(platform);
  for (const adapter of getArray(platformAdapters.adapters)) {
    const name = text(asRecord(adapter).name);
    if (name) connected.add(name);
  }
  const allPlatforms = new Set(connected);
  const configuredCounts = new Map<string, number>();
  for (const persona of getArray(asRecord(personasValue).personas)) {
    for (const platform of getStrings(asRecord(persona).platforms)) {
      configuredCounts.set(platform, (configuredCounts.get(platform) ?? 0) + 1);
      allPlatforms.add(platform);
    }
  }
  return [...allPlatforms].sort().map(platform => {
    const configured = configuredCounts.get(platform) ?? 0;
    const configuredLabel = locale === 'zh-CN' ? `已配置 ${configured} 个角色` : configured === 1 ? '1 configured persona' : `${configured} configured personas`;
    return [platform, `${connected.has(platform) ? (locale === 'zh-CN' ? '已连接' : 'Connected') : configuredLabelForPlatform(locale)}; ${configuredLabel}`];
  });
}

function secretEvidence(secretsValue: unknown, healthSecretRegistry: Record<string, unknown>, overviewSecretRegistry: Record<string, unknown>) {
  const secrets = getArray(asRecord(secretsValue).secrets);
  const total = firstNumber([healthSecretRegistry.total, overviewSecretRegistry.total, secrets.length]);
  const resolved = firstNumber([healthSecretRegistry.resolved, overviewSecretRegistry.resolved, secrets.filter(secret => text(asRecord(secret).status) === 'resolved').length]);
  return {
    total,
    resolved,
    missing: firstNumber([healthSecretRegistry.missing, overviewSecretRegistry.missing, secrets.filter(secret => text(asRecord(secret).status) === 'missing').length]),
    unsupported: firstNumber([healthSecretRegistry.unsupported, overviewSecretRegistry.unsupported, secrets.filter(secret => text(asRecord(secret).status) === 'unsupported').length]),
  };
}

function revealAuditEvidence(auditValue: unknown) {
  const events = getArray(asRecord(auditValue).events);
  const latest = asRecord(events[0]);
  const timestamp = text(latest.timestamp);
  const actor = text(latest.actor);
  const action = text(latest.action);
  return {
    count: events.length,
    latest: [timestamp, actor, action].filter(Boolean).join(' · '),
  };
}

function cutoverChecklistRows(releaseOperations: Record<string, unknown>, locale: SystemLocale): SystemCutoverChecklistItem[] {
  const copy = getSystemSettingsCopy(locale);
  const rows = getArray(releaseOperations.cutover_checklist).map(item => {
    const row = asRecord(item);
    const id = text(row.id) || text(row.label);
    return {
      id,
      label: localizedChecklistLabel(id, text(row.label), locale),
      status: localizedState(text(row.status) || 'contract_gap', locale),
      evidence: text(row.evidence) || copy.states.needsBackendDTO,
    };
  }).filter(item => item.id);
  if (rows.length) return rows;
  return [
    { id: 'build-version', label: copy.checklist.buildVersion, status: copy.states.partialData, evidence: copy.states.needsBackendDTO },
    { id: 'frontend-asset-freshness', label: copy.checklist.frontendAssetFreshness, status: copy.states.contractGap, evidence: copy.reasons.backendAssetContractGap },
    { id: 'auth-guard', label: copy.checklist.authGuard, status: copy.states.available, evidence: copy.evidence.authGuardClient },
    { id: 'legacy-frontend-retired', label: copy.checklist.legacyFrontendRetired, status: copy.states.partialData, evidence: copy.states.needsBackendDTO },
    { id: 'sensitive-reveal-audit', label: copy.checklist.sensitiveRevealAudit, status: copy.states.contractGap, evidence: copy.reasons.sensitiveRevealContractGap },
    { id: 'release-operation-preview', label: copy.checklist.releaseOperationPreview, status: copy.states.contractGap, evidence: copy.reasons.previewConfirmationUnavailable },
  ];
}

function localizedChecklistLabel(id: string, fallback: string, locale: SystemLocale) {
  const checklist = getSystemSettingsCopy(locale).checklist;
  if (id === 'build-version') return checklist.buildVersion;
  if (id === 'frontend-asset-freshness') return checklist.frontendAssetFreshness;
  if (id === 'auth-guard') return checklist.authGuard;
  if (id === 'legacy-frontend-retired') return checklist.legacyFrontendRetired;
  if (id === 'sensitive-reveal-audit') return checklist.sensitiveRevealAudit;
  if (id === 'release-operation-preview') return checklist.releaseOperationPreview;
  return fallback || id;
}

function guardedActions(actionsValue: unknown, locale: SystemLocale): SystemActionGuard[] {
  const copy = getSystemSettingsCopy(locale);
  const reason = copy.reasons.guardUnavailable;
  const actions = getArray(actionsValue).map(action => {
    const row = asRecord(action);
    const previewSupported = row.preview_supported === true;
    const confirmationRequired = row.confirmation_required === true;
    const enabled = row.enabled === true && previewSupported && confirmationRequired;
    const id = text(row.id) || 'unknown';
    return {
      id,
      label: localizedActionLabel(id, text(row.label), locale),
      state: enabled ? copy.states.previewRequired : copy.states.disabled,
      reason: enabled ? copy.reasons.previewRequired : localizedGuardReason(text(row.disabled_reason), locale) || reason,
      enabled,
      previewSupported,
      confirmationRequired,
      confirmationText: text(row.confirmation_text) || id.toUpperCase(),
    };
  }).filter(action => action.id !== 'unknown');
  if (actions.length) return actions;
  return [
    actionGuardFallback('restart', copy.actions.restart, reason, locale),
    actionGuardFallback('reset', copy.actions.reset, reason, locale),
    actionGuardFallback('flush', copy.actions.flush, reason, locale),
    actionGuardFallback('rollback', copy.actions.rollback, reason, locale),
  ];
}

function actionGuardFallback(id: string, label: string, reason: string, locale: SystemLocale): SystemActionGuard {
  const copy = getSystemSettingsCopy(locale);
  return {
    id,
    label,
    state: copy.states.disabled,
    reason,
    enabled: false,
    previewSupported: false,
    confirmationRequired: true,
    confirmationText: id.toUpperCase(),
  };
}

function rowsToObject(rows: Array<[string, string]>) {
  return Object.fromEntries(rows.map(([label, value]) => [label, value]));
}

function partialNotices(errors: Record<string, string> | undefined, deployment: ReturnType<typeof deploymentEvidence>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  const ignoredLabels = new Set(['secret reveal audit']);
  const notices = Object.entries(errors ?? {})
    .filter(([label]) => !ignoredLabels.has(label))
    .map(([label, message]) => `${label}${copy.notices.separator}${localizedErrorMessage(message, locale)}`);
  if (deployment.tone !== 'green') {
    notices.push(`${copy.notices.deploymentFreshnessPrefix}${copy.notices.separator}${deployment.evidence}${copy.notices.detailSeparator}${copy.notices.deploymentFreshnessSuffix}`);
  }
  return notices;
}


function localizedGuardReason(reason: string, locale: SystemLocale) {
  if (!reason) return '';
  const copy = getSystemSettingsCopy(locale);
  const normalized = reason.toLowerCase().trim();
  if (normalized.includes('backend preview unavailable')) return copy.reasons.backendPreviewUnavailable;
  if (normalized.includes('preview plus explicit confirmation') || normalized.includes('preview + confirmation')) return copy.reasons.guardUnavailable;
  return locale === 'zh-CN' ? copy.reasons.guardUnavailable : reason;
}

function localizedErrorMessage(message: string, locale: SystemLocale) {
  if (locale !== 'zh-CN') return message;
  const normalized = message.toLowerCase().trim();
  if (normalized.includes('file not found')) return '文件未找到';
  if (normalized.includes('control plane not configured')) return '控制平面未配置';
  if (normalized.includes('not available') || normalized.includes('unavailable')) return '不可用';
  if (normalized.includes('unauthorized')) return '未授权';
  return getSystemSettingsCopy(locale).notices.fallbackError;
}

function localizedEvidence(evidence: string, locale: SystemLocale) {
  if (locale !== 'zh-CN') return evidence;
  const normalized = evidence.toLowerCase().trim();
  const copy = getSystemSettingsCopy(locale);
  if (normalized === 'backend aggregate dto') return copy.evidence.backendAggregateDTO;
  if (normalized === 'asset commit matches expected target') return copy.evidence.assetCommitMatchesExpectedTarget;
  if (normalized === 'asset commit differs from expected target' || normalized === 'asset commit differs from target commit') return copy.evidence.assetCommitDiffersExpectedTarget;
  if (normalized === 'asset commit/build metadata unavailable') return copy.evidence.assetCommitBuildMetadataUnavailable;
  if (normalized === 'web console assets are not embedded') return 'Web 控制台资产未嵌入';
  if (normalized.includes('embedded console v2 assets fingerprinted')) return 'Console v2 嵌入资产已生成指纹，并由后端提供发布证据';
  if (normalized.includes('web console assets are embedded')) return 'Web 控制台资产已嵌入，但后端未暴露资产提交/指纹';
  return evidence;
}

function authMode(status: Record<string, unknown>, auth: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (typeof auth.mode === 'string') return auth.mode;
  if (typeof status.auth_mode === 'string') return status.auth_mode;
  if (typeof status.management_token_configured === 'boolean') {
    return status.management_token_configured ? (locale === 'zh-CN' ? '需要管理令牌' : 'Management token required') : (locale === 'zh-CN' ? '未配置管理令牌' : 'No management token configured');
  }
  return copy.states.partialModeUnavailable;
}

function httpAuthErrorStatus(auth: Record<string, unknown>, client: SystemSources['authClient'] | undefined, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  const httpErrors = asRecord(auth.http_errors);
  const backendEvidence = [text(httpErrors.unauthorized), text(httpErrors.forbidden)].filter(Boolean).join(' · ');
  const clientEvidence = client?.handlesAuthErrors ? copy.states.clientClearsPersistedToken : copy.states.needsBackendDTO;
  return [clientEvidence, backendEvidence].filter(Boolean).join(' · ');
}

function statusWithEvidence(row: Record<string, unknown>, fallback: string) {
  const status = text(row.status) || fallback;
  const evidence = text(row.evidence);
  if (!evidence) return status;
  return `${status} · ${evidence}`;
}

function boolRequiredLabel(value: unknown, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (value === true) return copy.states.required;
  if (value === false) return copy.states.notRequired;
  return copy.states.requiredByPolicy;
}

function revealSupportLabel(row: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (row.supported === true) return copy.states.available;
  if (row.supported === false) return copy.states.contractGap;
  return copy.states.needsBackendDTO;
}

function tokenStatus(token: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (!Object.keys(token).length) return copy.states.partialStatusUnavailable;
  return [presentLabel(token.present, locale), text(token.mode), text(token.scope), text(token.freshness)].filter(Boolean).join(' · ');
}

function serviceTokenStatus(token: Record<string, unknown>, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (!Object.keys(token).length) return copy.states.partialStatusUnavailable;
  const count = numberValue(token.count);
  const scopes = getStrings(token.scopes);
  const allowedRoutes = getStrings(token.allowed_routes);
  const constraints = getStrings(token.constraints);
  const scopeText = scopes.length ? scopes.join(', ') : text(token.scope);
  const routeText = allowedRoutes.length ? `routes: ${allowedRoutes.join(', ')}` : '';
  const constraintText = constraints.length ? `constraints: ${constraints.join(', ')}` : '';
  return [presentLabel(token.present, locale), formatSystemCopy(copy.states.configuredCount, { count }), text(token.mode), scopeText, routeText, constraintText, text(token.freshness)].filter(Boolean).join(' · ');
}

function presentLabel(value: unknown, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (value === true) return copy.states.present;
  if (value === false) return copy.states.notPresent;
  return copy.states.unknownPresence;
}

function configuredLabel(value: unknown, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (value === true) return copy.states.configured;
  if (value === false) return copy.states.notConfigured;
  return copy.states.unknown;
}

function booleanState(value: boolean, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  return value ? copy.states.available : copy.states.disabled;
}

function liveSessionCount(sessionsValue: unknown) {
  return getArray(asRecord(sessionsValue).sessions).filter(session => asRecord(session).live === true).length;
}

function runtimeProfileCount(sessionsValue: unknown) {
  return getArray(asRecord(sessionsValue).sessions).filter(session => Object.keys(asRecord(asRecord(session).runtime_profile)).length > 0).length;
}

function boolAvailability(value: unknown, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (value === true) return copy.states.available;
  if (value === false) return copy.states.unavailable;
  return copy.states.partialData;
}

function formatDurationValue(value: unknown, locale: SystemLocale) {
  const copy = getSystemSettingsCopy(locale);
  if (value === undefined || value === null || value === '') return copy.states.unavailable;
  const secondsTotal = numberValue(value);
  return formatDuration(secondsTotal);
}

function localizedState(value: string, locale: SystemLocale) {
  const normalized = value.toLowerCase().replace(/[\s_-]+/g, '_');
  if (locale === 'zh-CN') {
    const zh: Record<string, string> = {
      available: '可用',
      contract_gap: '契约缺口',
      fresh: '最新',
      not_configured: '未配置',
      partial: '部分数据',
      partial_data: '部分数据',
      reachable: '可访问',
      running: '运行中',
      stale: '陈旧',
      unknown: '未知',
      unavailable: '不可用',
    };
    return zh[normalized] ?? value;
  }
  return normalized.split('_').filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function configuredLabelForPlatform(locale: SystemLocale) {
  return locale === 'zh-CN' ? '已配置' : 'Configured';
}

function localizedActionLabel(id: string, fallback: string, locale: SystemLocale) {
  const actionCopy = getSystemSettingsCopy(locale).actions;
  if (id === 'restart') return actionCopy.restart;
  if (id === 'reset') return actionCopy.reset;
  if (id === 'reload') return actionCopy.reload;
  if (id === 'flush') return actionCopy.flush;
  if (id === 'rollback') return actionCopy.rollback;
  return fallback || actionCopy.unknown;
}

function formatDuration(value: number) {
  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = value % 60;
  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : '', `${seconds}s`].filter(Boolean).join(' ');
}

function sameCommit(left: string, right: string) {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  return a === b || a.startsWith(b) || b.startsWith(a);
}

function firstValue(record: Record<string, unknown>, keys: string[], fallback: number) {
  for (const key of keys) {
    if (record[key] !== undefined) return record[key];
  }
  return fallback;
}

function firstText(values: unknown[]) {
  for (const value of values) {
    const next = text(value);
    if (next) return next;
  }
  return '';
}

function firstNumber(values: unknown[]) {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return 0;
}

function numberWithDefault(value: unknown, fallback: number) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function booleanValue(value: unknown, fallback: boolean) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return fallback;
}

function numberText(value: unknown) {
  return String(numberValue(value));
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function getStrings(value: unknown) {
  return getArray(value).map(item => text(item)).filter(Boolean);
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown) {
  return typeof value === 'string' ? value : value === undefined || value === null ? '' : String(value);
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}
